import React, { useMemo, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTheme } from "@mui/material/styles";
import {
    CELL_SIZE,
    CELL_SPACING,
    NON_EMPTY_CELL_SCALE
} from '../../utils/dateUtils';
import { getTextColor } from '../../../../utils/colorUtils';

/**
 * Individual day cell component for the calendar
 *
 * @param {Object} props
 * @param {Date} props.date - Date object for this cell
 * @param {Array} props.items - Array of items scheduled for this date
 * @param {Array} props.batchRanges - Array of batch send date ranges for highlighting
 * @param {Function} props.onDayClick - Handler for clicking the day
 * @param {boolean} props.isMoveMode - Whether in move mode
 * @param {Object} props.contentTypeColors - Map of content types to colors
 * @returns {JSX.Element}
 */

const CalendarDay = ({ date, items, batchRanges = [], onDayClick, isMoveMode, contentTypeColors }) => {
    const theme = useTheme();
    const lightMode = theme.palette.mode === 'light';

    // Check if this date is today or in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const cellDate = new Date(date);
    cellDate.setHours(0, 0, 0, 0);
    const isToday = today.getTime() === cellDate.getTime();
    const isPastOrToday = cellDate <= today;

    const handleClick = useCallback(() => {
        // Allow click if date is in the future, or if past/today with items (read-only view)
        if (!isPastOrToday || items.length > 0) {
            onDayClick(date, items);
        }
    }, [date, items, onDayClick, isPastOrToday]);

    // Compute batch range highlighting info - handles multiple overlapping ranges
    const batchHighlightInfo = useMemo(() => {
        if (!batchRanges || batchRanges.length === 0) return null;

        const cellDateMs = cellDate.getTime();
        const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
        const isMonday = dayOfWeek === 1;
        const isSunday = dayOfWeek === 0;

        // Find ALL ranges this date falls within
        const overlappingRanges = [];

        for (const range of batchRanges) {
            const startMs = range.startDate.getTime();
            const endMs = range.endDate.getTime();

            if (cellDateMs >= startMs && cellDateMs <= endMs) {
                const isRangeStart = cellDateMs === startMs;
                const isRangeEnd = cellDateMs === endMs;

                // Check if previous/next days are in range (for highlight extension)
                const prevDay = new Date(cellDate);
                prevDay.setDate(prevDay.getDate() - 1);
                const nextDay = new Date(cellDate);
                nextDay.setDate(nextDay.getDate() + 1);

                const prevDayInRange = prevDay.getTime() >= startMs && prevDay.getTime() <= endMs;
                const nextDayInRange = nextDay.getTime() >= startMs && nextDay.getTime() <= endMs;

                overlappingRanges.push({
                    ...range,
                    isRangeStart,
                    isRangeEnd,
                    prevDayInRange,
                    nextDayInRange
                });
            }
        }

        if (overlappingRanges.length === 0) return null;

        // Determine combined rounding/extension logic for multiple overlapping ranges
        const allRangesStartHere = overlappingRanges.every(r => r.isRangeStart);
        const anyRangeEndsHere = overlappingRanges.some(r => r.isRangeEnd);
        const allRangesEndHere = overlappingRanges.every(r => r.isRangeEnd);

        // For extension: extend if ANY range extends in that direction
        const anyPrevDayInRange = overlappingRanges.some(r => r.prevDayInRange);
        const anyNextDayInRange = overlappingRanges.some(r => r.nextDayInRange);

        // Round left: all ranges start here OR it's Monday (week boundary)
        const roundLeft = allRangesStartHere || isMonday;
        // Round right: all ranges end here OR it's Sunday (week boundary)
        const roundRight = allRangesEndHere || isSunday;

        // Extend to cover gaps between cells: extend if not all ranges start/end here
        // and we're not at a week boundary, and there's content extending in that direction
        const extendLeft = !allRangesStartHere && !isMonday && anyPrevDayInRange;
        const extendRight = !allRangesEndHere && !isSunday && anyNextDayInRange;

        return {
            isInRange: true,
            ranges: overlappingRanges,
            roundLeft,
            roundRight,
            extendLeft,
            extendRight,
            isRangeEnd: anyRangeEndsHere
        };
    }, [batchRanges, cellDate, date]);

    // Determine if the cell should show content
    const isEmpty = items.length === 0;

    // Calculate cell size with scaling for non-empty cells
    const cellScale = !isEmpty ? NON_EMPTY_CELL_SCALE : 1;

    // Calculate inverse scale for the highlight to counteract parent scaling
    const highlightInverseScale = !isEmpty ? 1 / NON_EMPTY_CELL_SCALE : 1;

    // Color gradient for multiple items
    // Uses vertical gradient (180deg) for horizontal symmetry - needed for batch send bars to align
    const getMultipleItemsColor = useCallback((items) => {
        if (items.length === 0) return 'transparent';
        if (items.length === 1) return contentTypeColors[items[0].contentType] || '#999';

        // Sort by contentType for consistent gradient ordering
        const sortedItems = [...items].sort((a, b) => a.contentType.localeCompare(b.contentType));
        const colors = sortedItems.map(item => contentTypeColors[item.contentType] || '#999');
        const uniqueColors = [...new Set(colors)];
        if (uniqueColors.length === 1) return uniqueColors[0];
        return `linear-gradient(180deg, ${uniqueColors.join(', ')})`;
    }, [contentTypeColors]);

    // Get combined background color/gradient from both direct items and overlapping batch ranges
    // This ensures direct sends within a batch range show a gradient combining both
    const getCombinedBackground = useCallback((items, batchRanges, forFullCell = true) => {
        // Collect all content types from direct items and batch ranges
        const allContentTypes = [];

        items.forEach(item => {
            allContentTypes.push(item.contentType);
        });

        // Add batch range content types (avoid duplicating if batch item is already in items)
        if (batchRanges && batchRanges.length > 0) {
            batchRanges.forEach(range => {
                const alreadyInItems = items.some(item => item.id === range.id);
                if (!alreadyInItems) {
                    allContentTypes.push(range.contentType);
                }
            });
        }

        // Sort by contentType for consistent gradient ordering, then map to colors
        allContentTypes.sort((a, b) => a.localeCompare(b));
        const allColors = allContentTypes.map(type => contentTypeColors[type] || '#999');

        // Get unique colors (preserves sorted order since Set keeps insertion order)
        const uniqueColors = [...new Set(allColors)];

        if (uniqueColors.length === 0) return 'transparent';
        if (uniqueColors.length === 1) return uniqueColors[0];

        if (forFullCell) {
            // Standard vertical gradient for full cell backgrounds
            return `linear-gradient(180deg, ${uniqueColors.join(', ')})`;
        }

        // For highlight bar - use adjusted stops (same logic as getHighlightBackground)
        const stops = uniqueColors.map((color, index) => {
            const fullPercent = uniqueColors.length === 1
                ? 50
                : (index / (uniqueColors.length - 1)) * 100;
            const localPercent = ((fullPercent - 35) / 30) * 100;
            return `${color} ${localPercent.toFixed(2)}%`;
        });

        return `linear-gradient(180deg, ${stops.join(', ')})`;
    }, [contentTypeColors]);

    // Get highlight color/gradient based on overlapping batch ranges
    // Uses vertical gradient (180deg) for seamless horizontal tiling across cells
    // 
    // The highlight bar is 30% height, centered at 50% (spans 35%-65% of cell height).
    // When forFullCell=false, we adjust the gradient stops so the bar displays only
    // the "middle slice" of the gradient, making it align seamlessly with full-cell gradients.
    const getHighlightBackground = useCallback((ranges, forFullCell = false) => {
        if (!ranges || ranges.length === 0) return 'transparent';

        // Sort ranges by contentType for consistent gradient ordering, then map to colors
        const sortedRanges = [...ranges].sort((a, b) => a.contentType.localeCompare(b.contentType));
        const colors = sortedRanges.map(r => contentTypeColors[r.contentType] || '#4caf50');
        const uniqueColors = [...new Set(colors)];

        if (uniqueColors.length === 1) {
            return uniqueColors[0];
        }

        if (forFullCell) {
            // Standard gradient for full cell backgrounds (0% to 100%)
            return `linear-gradient(180deg, ${uniqueColors.join(', ')})`;
        }

        // For the highlight bar (30% height, centered, spanning 35%-65% of cell):
        // We need to adjust gradient stops so the bar shows only the middle portion
        // of what would be a full-cell gradient.
        //
        // Mapping formula: localPercent = (fullPercent - 35) / 30 * 100
        // This maps: 35% (full) -> 0% (local), 65% (full) -> 100% (local)
        //
        // For colors at their normal positions (0%, 50%, 100%, etc.):
        //   0%   -> -116.67%  (above the bar)
        //   35%  -> 0%        (top of bar)
        //   50%  -> 50%       (middle of bar)
        //   65%  -> 100%      (bottom of bar)
        //   100% -> 216.67%   (below the bar)
        const stops = uniqueColors.map((color, index) => {
            const fullPercent = uniqueColors.length === 1
                ? 50
                : (index / (uniqueColors.length - 1)) * 100;
            const localPercent = ((fullPercent - 35) / 30) * 100;
            return `${color} ${localPercent.toFixed(2)}%`;
        });

        return `linear-gradient(180deg, ${stops.join(', ')})`;
    }, [contentTypeColors]);

    // Get single highlight color (for backward compatibility)
    const getHighlightColor = useCallback((contentType) => {
        return contentTypeColors[contentType] || '#4caf50';
    }, [contentTypeColors]);

    // Determine background color based on items, date, and batch range end
    const backgroundColor = useMemo(() => {
        // Handle past/today dates - also consider batch ranges
        if (isPastOrToday) {
            if (isEmpty) {
                // Empty past day - check if in batch range
                if (batchHighlightInfo?.isInRange) {
                    return getHighlightBackground(batchHighlightInfo.ranges, true);
                }
                return '#f0f0f0';
            }
            // Non-empty past day - combine items with batch range if applicable
            if (batchHighlightInfo?.isInRange) {
                return getCombinedBackground(items, batchHighlightInfo.ranges, true);
            }
            return getMultipleItemsColor(items);
        }
        if (isMoveMode) return 'rgba(144, 202, 249, 0.15)';
        // Fill the final day of a batch range with the highlight color/gradient (if empty)
        // Use forFullCell=true since this gradient spans the entire cell
        if (isEmpty && batchHighlightInfo?.isInRange) {
            return getHighlightBackground(batchHighlightInfo.ranges, true);
        }
        if (isEmpty) return theme.palette.mode === 'light' ? '#e0e0e0' : '#b0b0b0';
        // For non-empty cells, combine item colors with any overlapping batch range colors
        if (items.length > 0) {
            return getCombinedBackground(items, batchHighlightInfo?.ranges, true);
        }
        return 'transparent';
    }, [items, isMoveMode, isEmpty, getMultipleItemsColor, isPastOrToday, theme.palette.mode, batchHighlightInfo, getHighlightBackground, getCombinedBackground]);

    // Determine text color (using getTextColor for content items)
    const textColor = useMemo(() => {
        // For empty past days, check if in batch range first
        if (isPastOrToday && isEmpty) {
            if (batchHighlightInfo?.isInRange) {
                const colors = batchHighlightInfo.ranges.map(r => contentTypeColors[r.contentType] || '#4caf50');
                const uniqueColors = [...new Set(colors)];
                if (uniqueColors.length > 1) {
                    return '#fff';
                }
                return getTextColor(uniqueColors[0]);
            }
            return '#999';
        }
        // For batch end cells that are empty, use appropriate text color for the highlight background
        if (isEmpty && batchHighlightInfo?.isInRange) {
            // For multiple ranges with different colors (gradient), use white text
            const colors = batchHighlightInfo.ranges.map(r => contentTypeColors[r.contentType] || '#4caf50');
            const uniqueColors = [...new Set(colors)];
            if (uniqueColors.length > 1) {
                return '#fff';
            }
            // Single color - use calculated text color
            return getTextColor(uniqueColors[0]);
        }
        if (items.length > 0 && !isMoveMode) {
            // Collect all colors from items and overlapping batch ranges
            const allColors = [];
            items.forEach(item => {
                allColors.push(contentTypeColors[item.contentType] || '#999');
            });
            if (batchHighlightInfo?.ranges) {
                batchHighlightInfo.ranges.forEach(range => {
                    const alreadyInItems = items.some(item => item.id === range.id);
                    if (!alreadyInItems) {
                        allColors.push(contentTypeColors[range.contentType] || '#4caf50');
                    }
                });
            }
            const uniqueColors = [...new Set(allColors)];

            if (uniqueColors.length === 1) {
                return getTextColor(uniqueColors[0]);
            }
            // For gradient backgrounds, use white text
            return '#fff';
        }
        return 'inherit';
    }, [items, isMoveMode, isPastOrToday, isEmpty, contentTypeColors, batchHighlightInfo]);

    // Show indicators for multiple items
    const itemCount = items.length;

    // Calculate extension amounts (half of the gap between cells)
    const gapExtension = (parseInt(CELL_SPACING) / 2) * (1 / highlightInverseScale);

    return (
        <Box
            onClick={handleClick}
            sx={{
                width: CELL_SIZE,
                height: CELL_SIZE,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '6px',
                cursor: (isPastOrToday && items.length === 0) ? 'default' : 'pointer',
                position: 'relative',
                backgroundImage: backgroundColor.includes('linear-gradient') ? backgroundColor : 'none',
                backgroundColor: !backgroundColor.includes('linear-gradient') ? backgroundColor : 'transparent',
                transition: 'all 0.2s',
                transform: isEmpty && !isMoveMode ? 'scale(1)' : `scale(${cellScale})`,
                zIndex: isEmpty ? 1 : 2,
                opacity: isPastOrToday ? 0.7 : 1,
                // Use outline instead of border for today - outline doesn't affect box model
                // so batch highlight bars won't be cut off
                outline: isToday ? '2px solid #ff9800' : 'none',
                outlineOffset: '-2px',
                ...(isPastOrToday && {
                    backgroundImage: isEmpty ?
                        'repeating-linear-gradient(45deg, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.1) 5px, rgba(0, 0, 0, 0) 5px, rgba(0, 0, 0, 0) 10px)' :
                        (backgroundColor.includes('linear-gradient') ? backgroundColor : 'none'),
                }),
                '&:hover': {
                    opacity: isPastOrToday ? 0.7 : 1,
                    transform: isPastOrToday ?
                        (isEmpty && !isMoveMode ? 'scale(1)' : `scale(${cellScale})`) :
                        (isEmpty && !isMoveMode ? 'scale(1.05)' : `scale(${cellScale + 0.1})`),
                    boxShadow: isPastOrToday ? 'none' : theme.customShadows.z1,
                    backgroundColor: isPastOrToday ?
                        (!backgroundColor.includes('linear-gradient') ? backgroundColor : 'transparent') :
                        (isMoveMode ? 'rgba(144, 202, 249, 0.3)' :
                            (isEmpty && batchHighlightInfo?.isInRange ? undefined :
                                (isEmpty ? (lightMode ? '#cccccc' : '#707070') : undefined))),
                    zIndex: isPastOrToday ? (isEmpty ? 1 : 2) : 3
                }
            }}
        >
            {/* Batch send range highlight - supports multiple overlapping ranges with gradient */}
            {/* Uses adjusted gradient (forFullCell=false) so colors align with adjacent cell backgrounds */}
            {/* When items exist on this date, combines their colors with batch range colors */}
            {batchHighlightInfo?.isInRange && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        transform: `translateY(-50%) scale(${highlightInverseScale})`,
                        height: '30%',
                        left: batchHighlightInfo.extendLeft ? `-${gapExtension}px` : '0',
                        right: batchHighlightInfo.extendRight ? `-${gapExtension}px` : '0',
                        background: items.length > 0
                            ? getCombinedBackground(items, batchHighlightInfo.ranges, false)
                            : getHighlightBackground(batchHighlightInfo.ranges, false),
                        borderTopLeftRadius: batchHighlightInfo.roundLeft ? '6px' : '0',
                        borderBottomLeftRadius: batchHighlightInfo.roundLeft ? '6px' : '0',
                        borderTopRightRadius: batchHighlightInfo.roundRight ? '6px' : '0',
                        borderBottomRightRadius: batchHighlightInfo.roundRight ? '6px' : '0',
                        zIndex: 0,
                        pointerEvents: 'none',
                    }}
                />
            )}

            <Typography
                variant="caption"
                sx={{
                    color: textColor,
                    fontWeight: (items.length > 0 || batchHighlightInfo?.isRangeEnd) ? 'bold' : 'normal',
                    zIndex: 1
                }}
            >
                {date.getDate()}
            </Typography>

            {/* Move mode indicator */}
            {isMoveMode && (
                <Box sx={{
                    position: 'absolute',
                    top: '1.5px',
                    right: '1.5px',
                    color: '#2196f3',
                    fontSize: '11px'
                }}>
                    <AddIcon fontSize="inherit" />
                </Box>
            )}

            {/* Count badge for multiple items */}
            {itemCount > 1 && (
                <Box sx={{
                    position: 'absolute',
                    bottom: '1.5px',
                    right: '1.5px',
                    bgcolor: 'rgba(255,255,255,0.8)',
                    color: '#333',
                    borderRadius: '50%',
                    width: '9px',
                    height: '9px',
                    fontSize: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold'
                }}>
                    {itemCount}
                </Box>
            )}
        </Box>
    );
};

export default React.memo(CalendarDay);